import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { 
  FaSearch, 
  FaUser,
  FaEnvelope,
  FaPhone,
  FaCheckCircle,
  FaClock,
  FaToggleOn,
  FaToggleOff
} from 'react-icons/fa';
import useSWR from 'swr';
import Fetcher from '../../lib/Fetcher';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import { formatName, toTitleCase } from '../../utils/dataTransformers';

function AllUsers() {
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (statusFilter) params.append('status', statusFilter);
    if (debouncedSearchQuery) params.append('search', debouncedSearchQuery);
    params.append('page', page.toString());
    params.append('limit', '12');
    return `/api/admin/users?${params.toString()}`;
  }, [statusFilter, debouncedSearchQuery, page]);

  const { data, error, mutate } = useSWR(buildQuery(), Fetcher);
  const users = data?.data?.users || [];
  const pagination = data?.data?.pagination || { total: 0, pages: 1 };

  const handleStatusChange = async (userId, newStatus) => {
    setLoading(userId);
    try {
      await HttpInterceptor.patch(`/api/admin/users/${userId}/status`, {
        status: newStatus
      });
      toast.success(`User status updated to ${newStatus}`);
      mutate();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    } finally {
      setLoading(null);
    }
  };

  // Memoize filtered users - filter is now handled by backend, but keeping for client-side fallback
  const filteredUsers = useMemo(() => {
    // If backend supports search, return users as-is
    // Otherwise, filter client-side as fallback
    const usersData = data?.data?.users || [];
    if (debouncedSearchQuery && usersData.length > 0) {
      const query = debouncedSearchQuery.toLowerCase();
      return usersData.filter(user => {
        return (
          user.name?.toLowerCase().includes(query) ||
          user.email?.toLowerCase().includes(query) ||
          user.mobile?.includes(debouncedSearchQuery)
        );
      });
    }
    return usersData;
  }, [data?.data?.users, debouncedSearchQuery]);

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Members</h1>
          <p className="text-gray-600">Manage all members and their accounts</p>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
            </select>
            <button
              onClick={() => {
                setStatusFilter('');
                setSearchQuery('');
                setDebouncedSearchQuery('');
                setPage(1);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Users Grid */}
        {error ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-red-600">Failed to load users</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <FaUser className="text-6xl text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Members Found</h2>
            <p className="text-gray-600">Try adjusting your filters or search query.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {filteredUsers.map((user) => (
                <div key={user._id} className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-3">
                        <FaUser className="text-green-600 text-xl" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">{formatName(user.name)}</h3>
                        <p className="text-sm text-gray-600">{toTitleCase(user.gender)}</p>
                      </div>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        user.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {user.status === 'active' ? (
                        <FaCheckCircle className="inline mr-1" />
                      ) : (
                        <FaClock className="inline mr-1" />
                      )}
                      {user.status}
                    </span>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <FaEnvelope className="mr-2 text-gray-400" />
                      <span className="truncate">{user.email}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <FaPhone className="mr-2 text-gray-400" />
                      <span>{user.mobile}</span>
                    </div>
                    {user.dateOfBirth && (
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">DOB:</span> {user.dateOfBirth}
                      </div>
                    )}
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleStatusChange(user._id, user.status === 'active' ? 'pending' : 'active')}
                      disabled={loading === user._id}
                      className={`flex-1 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${
                        user.status === 'active'
                          ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      {loading === user._id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : user.status === 'active' ? (
                        <>
                          <FaToggleOff className="mr-2" />
                          Deactivate
                        </>
                      ) : (
                        <>
                          <FaToggleOn className="mr-2" />
                          Activate
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex justify-center items-center space-x-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="text-gray-600">
                  Page {page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= pagination.pages}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AllUsers;

